import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SchoolBellEAPIService } from '@sbe-spinoff-client/schoolbell-e-api';
import { StaffOidcAuthService } from '@sbe-spinoff-client/staff-oidc-auth';

export type StaffInvitationStatus = 'Pending' | 'Accepted' | 'Rejected' | 'Cancelled' | 'Expired';

export interface StaffMe {
  staffId: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  role: string;
  invitationStatus: StaffInvitationStatus;
  /** 초대 수락 직후 부여된 권한이 하나도 없는 상태인지 — invitation.component.ts#accept() 참고. */
  hasEffectiveScopes: boolean;
}

export interface PendingInvitation {
  invitationId: string;
  role: string;
  invitedByStaffId: string;
  expiresAt: string;
}

/** `POST staff/auth/login` 응답 — `StaffMe` 에 스핀오프 세션의 access token 이 더해진다(refresh 토큰은 HttpOnly 쿠키로만 온다). */
type StaffLoginResponse = StaffMe & { accessToken: string; accessExpiresInSeconds: number };

/**
 * schoolbell-e-api `/staff/auth/*`(자체 로그인 — User.phone + password 인증 후 Staff 소속 확인)
 * 를 소비하는 클라이언트 쪽 책임 — 생성된 `SchoolBellEAPIService` 를 감싼다
 * (`OAuthClientReviewService` 와 동일 패턴).
 *
 * 세션은 사용자 웹·RP 스태프 웹과 같은 **스핀오프 세션**이다: access token 은 JS 메모리에만 두고
 * `Authorization: Bearer` 로 보내며, refresh token 은 로그인 응답이 심은 HttpOnly 쿠키(`spinoff-staff-refresh`)다.
 * 새로고침하면 refresh 쿠키로 access token 을 복원한다. 토큰 보관·refresh 단일화·401 재시도·로그아웃은 공용
 * `StaffOidcAuthService`/`staffBearerInterceptor`(`@sbe-spinoff-client/staff-oidc-auth`)가 맡고, 이 서비스는 비밀번호
 * 로그인과 앱 소유 응답(`StaffMe`)·초대 흐름만 더한다.
 *
 * `staff` signal 은 로그인/`me()` 성공 시점의 표시용 캐시일 뿐 세션의 SoT가 아니다 — 실제
 * 인가는 항상 서버(토큰 검증)가 한다.
 */
@Injectable({ providedIn: 'root' })
export class StaffAuthService {
  private readonly api = inject(SchoolBellEAPIService);
  private readonly session = inject(StaffOidcAuthService);

  private readonly _staff = signal<StaffMe | null>(null);
  readonly staff = this._staff.asReadonly();

  /**
   * 최초 세션 확인 완료 여부 — 성공·실패 무관하게 첫 확인 후 true(`injectProtectedEntry` 기반 부팅 로딩 오버레이 게이팅용).
   * `logout()`에서는 되돌리지 않는다 — 되돌리면 guard 없는 경로(`/welcome` 등)에서 오버레이가 다시 걸려 영구히 멈춘다
   * (`StaffOidcAuthService.logout()` 주석과 같은 근거).
   */
  readonly sessionChecked = this.session.sessionChecked;

  /**
   * 이 SPA 인스턴스에서 방금 비밀번호로 로그인했는지 — OIDC interaction 확인 화면
   * (`oauth-authorize`)이 "방금 인증한 같은 사용자"에게 계정 확인을 또 묻지 않도록 읽는다
   * (OP `always_confirm_existing_account` 시나리오 1과 동일). 메모리에만 두므로 새로고침·재방문
   * (= 이전 방문의 세션 흔적)이면 false 라 확인 화면이 뜬다.
   */
  private justLoggedIn = false;

  /** `true`를 돌려주면 플래그를 소비한다(1회용). */
  consumeJustLoggedIn(): boolean {
    const value = this.justLoggedIn;
    this.justLoggedIn = false;
    return value;
  }

  async login(phone: string, password: string): Promise<StaffMe> {
    const { accessToken, accessExpiresInSeconds: _expiresIn, ...me } = await firstValueFrom(this.api.staffAuthControllerLogin<StaffLoginResponse>({ phone, password }));
    this.session.adoptSession(accessToken);
    this._staff.set(me);
    this.justLoggedIn = true;
    return me;
  }

  /**
   * 세션을 확인하고 현재 staff 를 돌려준다. access token 이 없으면(새로고침·재방문) refresh 쿠키로 복원한 뒤 `GET /staff/auth/me` 를 읽는다.
   * 세션이 없거나 만료면 던진다(`staffAuthGuard` 가 `/login` 으로 보낸다).
   */
  async me(): Promise<StaffMe> {
    const ok = await this.session.loadMe();
    const me = this.session.me() as StaffMe | null;
    if (!ok || !me) {
      this._staff.set(null);
      throw new Error('staff 세션이 없습니다');
    }
    this._staff.set(me);
    return me;
  }

  async logout(): Promise<void> {
    try {
      await this.session.logout();
    } finally {
      this._staff.set(null);
      this.justLoggedIn = false;
    }
  }

  getInvitation(): Promise<PendingInvitation | null> {
    return firstValueFrom(this.api.staffAuthControllerInvitation<PendingInvitation | null>());
  }

  async acceptInvitation(): Promise<StaffMe> {
    const res = await firstValueFrom(this.api.staffAuthControllerAcceptInvitation<StaffMe>());
    this._staff.set(res);
    return res;
  }

  async rejectInvitation(reason?: string): Promise<void> {
    try {
      await firstValueFrom(this.api.staffAuthControllerRejectInvitation({ reason }));
    } finally {
      this._staff.set(null);
    }
  }

  /**
   * requiresStaff OIDC client(commerce-staff/sms-staff/grafana 등)의 interaction을 현재 staff
   * 세션으로 완료 — org/team 소속 검증·동의 화면 없이 즉시 로그인 처리(oidc-interaction.controller.ts
   * #interactionStaffLogin). 백엔드가 `@ApiOkResponse` 타입을 선언하지 않아(다른 컨트롤러와 동일
   * 사정, 위 login/me 참고) 응답 제네릭을 여기서 명시한다.
   */
  completeOidcInteraction(uid: string): Promise<{ redirectTo: string }> {
    return firstValueFrom(
      this.api.oidcInteractionControllerInteractionStaffLogin<{ redirectTo: string }>(uid),
    );
  }
}
